<template>
  <OfflineModal />
  <ScannerErrorModal :visible="isScannerDisconnected" />
  <NotFound v-if="isNotFound" :item="isNotFoundItem" />
  <template v-else>
    <AppHeader />
    <div id="App_Loading" v-loading.fullscreen="!initialized">
      <router-view v-if="initialized" />
    </div>
    <AppDialog />
  </template>
  <HardKeyDebugOverlay />
</template>

<script lang="ts">
import AppDialog from '@/components/AppDialog.vue'
import AppHeader from '@/components/AppHeader.vue'
import NotFound from '@/components/common/NotFound.vue'
import HardKeyDebugOverlay from '@/components/keyenceHT/HardKeyDebugOverlay.vue'
import OfflineModal from '@/components/OfflineModal.vue'
import ScannerErrorModal from '@/components/ScannerErrorModal.vue'
import {
  FETCH_PROFILE,
  FETCH_PROJECT,
  LOGOUT,

  RESET_PAGE_NOT_FOUND,
  SET_PAGE_NOT_FOUND,
} from '@/store/actions'
import { addMinutes, isAfter } from 'date-fns'
import isEmpty from 'lodash/isEmpty'
import ReactGA from 'react-ga4'
import { Options, Vue } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { LOCAL_STORAGE_ITEM, SMARTBARCODE_TYPE } from './utils/constants'
import { queueError } from './utils/errorLogger'
import { deleteCookie, getCookie, setCookie } from './utils/helpers'
import { scannerBridge, ScannerStatus } from './utils/scannerBridge'
import { IProject } from './utils/types/projectTypes'

@Options({
  components: {
    AppHeader,
    AppDialog,
    NotFound,
    OfflineModal,
    ScannerErrorModal,
    HardKeyDebugOverlay,
  },
  name: 'App',
})
export default class App extends Vue {
  isUpdateModalOpened = false
  remindUpdateDate = (undefined as unknown) as Date
  reconnectInterval: ReturnType<typeof setInterval> | null = null
  isScannerDisconnected = false

  get isNotFound() {
    return this.$store.state.isNotFound
  }

  get isNotFoundItem() {
    return this.$store.state.notFoundItem
  }

  get showHeader() {
    return this.$route.meta.header
  }

  get user() {
    return this.$store.state.profile.user
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get initialized() {
    return this.$store.state.project.initialized
  }

  redirectHome() {
    this.$router.replace({
      name: 'home',
    })
  }

  async mounted() {
    this.$store.dispatch(FETCH_PROFILE)
    this.$store.dispatch(FETCH_PROJECT)

    // Scanner Bridge Integration
    scannerBridge.onStatusChange((status: ScannerStatus) => {
      if (status.status === 'Connected') {
        this.isScannerDisconnected = false
        if (this.reconnectInterval) {
          clearInterval(this.reconnectInterval)
          this.reconnectInterval = null
        }
      } else {
        this.isScannerDisconnected = true
        const connectScannerError = this.$t('connect_fs_scanner_error')
        if (!this.reconnectInterval) {
          console.log('Attempting to reconnect scanner...')
          queueError('', {}, '', connectScannerError)
          this.reconnectInterval = setInterval(() => {
            scannerBridge.reconnect()
          }, 500)
        }
      }
    })
  }

  created() {
    if (process.env.VUE_APP_ENV === 'staging') {
      document.body.classList.add('env-staging')
    }
    document.addEventListener('onUpdateVersionNotification', ((async (evt: CustomEvent) => {
      const { isForceUpgrade } = evt.detail
      if (this.remindUpdateDate) {
        if (isAfter(new Date(), this.remindUpdateDate)) {
          await this.openUpdateDialog(isForceUpgrade)
        }
      } else {
        await this.openUpdateDialog(isForceUpgrade)
      }
    }) as unknown) as EventListener)
  }

  async openUpdateDialog(isForce = false) {
    if (!this.isUpdateModalOpened) {
      this.isUpdateModalOpened = true
      await this.$confirm(this.$t('confirm_update'), this.$t('info'), {
        confirmButtonText: this.$t('update'),
        confirmButtonClass: 'danger',
        cancelButtonText: this.$t('later'),
        showClose: !isForce,
        closeOnClickModal: !isForce,
        showCancelButton: !isForce,
      })
        .then(() => {
          this.confirmUpdateVersion()
        })
        .catch(() => this.remindUpdateLater())
      this.isUpdateModalOpened = false
    }
  }

  confirmUpdateVersion() {
    window.location.reload()
  }

  remindUpdateLater() {
    this.remindUpdateDate = addMinutes(new Date(), 15)
  }

  closeUpdateDialog() {
    this.isUpdateModalOpened = false
  }

  @Watch('initialized')
  onInitializedChange(initialized: boolean) {
    if (initialized && !this.project) {
      this.$store.dispatch(SET_PAGE_NOT_FOUND, { item: 'project' })
    }
  }

  @Watch('isAuth')
  onAuthChange(isAuth: boolean) {
    const isRestrictedPage = this.$route.meta.restricted
    if (!isAuth && isRestrictedPage) {
      this.redirectHome()
    }
    if (this.isAuth) {
      const lastAuthorizedSystem = getCookie(LOCAL_STORAGE_ITEM.LAST_AUTHORIZED_SYSTEM)
      if (!lastAuthorizedSystem) {
        setCookie(LOCAL_STORAGE_ITEM.LAST_AUTHORIZED_SYSTEM, SMARTBARCODE_TYPE.MOBILE, 365)
      } else {
        if (getCookie(LOCAL_STORAGE_ITEM.LAST_AUTHORIZED_SYSTEM) === SMARTBARCODE_TYPE.PORTAL) {
          this.$store.dispatch(LOGOUT)
          deleteCookie(LOCAL_STORAGE_ITEM.LAST_AUTHORIZED_SYSTEM)
        }
      }
    } else {
      deleteCookie(LOCAL_STORAGE_ITEM.LAST_AUTHORIZED_SYSTEM)
    }
  }

  @Watch('$route.params')
  onRouterChange() {
    this.$store.dispatch(RESET_PAGE_NOT_FOUND)
  }

  /** pages that don't need to check enterprise for display dialog */
  get exceptionPages() {
    return ['custom-html']
  }

  get isExceptionPage(): boolean {
    return this.exceptionPages.includes(this.$route.name)
  }

  @Watch('$route')
  @Watch('project')
  onRouteOrProjectChanged() {
    if (!this.project?.googleAnalyticsMeasurementId) return
    if (ReactGA.isInitialized) {
      ReactGA.reset()
    }

    ReactGA.initialize(
      (!isEmpty(this.project?.googleAnalyticsMeasurementId)
        ? this.project?.googleAnalyticsMeasurementId
        : process.env.VUE_APP_GA4_MEASUREMENT_ID) as string
    )
    ReactGA.send('pageview')
  }

  @Watch('project')
  onProjectChange(project: IProject) {
    if (project === null) return

    // Check if user belongs to the enterprise of project
    const profileEnterpriseId = this.$store.getters.enterpriseId
    // if (this.isAuth && profileEnterpriseId !== project.enterpriseId && !this.isExceptionPage) {
    //   this.$store.dispatch(LOGOUT)
    //   this.$store.dispatch(OPEN_DIALOG, {
    //     message: this.$t('Not belong to Enterprise', {
    //       company: this.$store.state.project.enterprise,
    //     }),
    //   })
    // }
  }
}
</script>

<style lang="scss">
@import '~@/assets/css/mixins.scss';

#app {
  @-moz-document url-prefix() {
    button:disabled {
      pointer-events: none;
    }
  }

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: calc(100vh - 106px);
  background-color: $body-bg;
  font-family: Noto Sans JP;
  font-size: $main-fsize;

  @media (min-width: 480px) {
    font-size: $main-fsize !important;
  }
}

a:-webkit-any-link {
  text-decoration: none;
}

body.env-staging,
body.env-staging #app {
  background-color: #ffe082;
}

.el-dialog.mode-operator-add {
  background-color: #9dc3e6;
}

.el-dialog.mode-operator-use {
  background-color: #a9d18e;
}

.el-dialog.mode-left-amount-collection {
  background-color: #f48fb1;
}

.el-message-box__message {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}
</style>
